import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { ResourceLoading } from "./ResourceLoading";
import { TWclassNames } from "./Div";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

const PMDialogComponent = (props) => {
  const {
    isLoading = false,
    title,
    children = "Content missing",
    onClose = () => {},
    open = false,
    size = "2xl",
    headerActions,
    closeOnOutsideClick,
    zIndex,
    /** Tighter padding, mobile-friendly full height, flex body for embedded panels (e.g. product selector) */
    dense = false,
    /** Extra classes merged onto `Dialog.Panel` (e.g. wider max-width on desktop) */
    panelClassName = "",
    /**
     * Extra classes on the scrollable/body wrapper around children.
     * Use e.g. `!overflow-hidden` when the child pins its own footer and
     * scrolls an inner region (so Lagre/Avbryt stay visible).
     */
    contentClassName = "",
    /** Form-dialog header (design §5): small accent label above the title. */
    eyebrow,
    /** Form-dialog header: one-line explanation under the title. */
    subtitle,
  } = props;

  const { t } = useTranslation();

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
  };

  const hasTitle = Boolean(title);
  const hasFormHeader = hasTitle && !dense && Boolean(eyebrow || subtitle);
  const headerControls = (
    <>
      {headerActions
        ?.filter((action) => !action.hidden)
        .map((action) => {
          if (action?.component) {
            return action.component;
          } else {
            if (action.path) {
              return (
                <Link
                  key={action.title}
                  to={action.path}
                  type="button"
                  className="pm-btn pm-btn--primary pm-btn--sm"
                >
                  <action.icon
                    className="-ml-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                  {action.label}
                </Link>
              );
            } else {
              return (
                <button
                  key={action.title}
                  onClick={action.action}
                  type="button"
                  className="pm-btn pm-btn--primary pm-btn--sm"
                >
                  <action.icon
                    className="-ml-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                  {action.label}
                </button>
              );
            }
          }
        })}
      {/* Close button  */}
      <button
        type="button"
        className="pm-dialog__close"
        onClick={onClose}
        aria-label={t("Close")}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </>
  );

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: zIndex ?? 2000 }}
          onClose={closeOnOutsideClick ? onClose : () => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pm-dialog__scrim pm-vv-fill transition-opacity" />
          </Transition.Child>

          <div className="pm-vv-fill z-10 overflow-hidden">
            <div className="flex min-h-full items-stretch justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={
                    dense
                      ? TWclassNames(
                          "pm-dialog__panel w-full max-w-full md:max-w-6xl lg:max-w-7xl transform rounded-none sm:rounded-[18px] p-0 sm:p-4 text-left align-middle transition-all",
                          "flex flex-col overflow-hidden h-[var(--vv-height,100dvh)] max-h-[var(--vv-height,100dvh)] sm:h-[720px] sm:max-h-[80vh] m-0 sm:mb-[5vh] sm:mt-[5vh]",
                          panelClassName
                        )
                      : TWclassNames(
                          "lg:" + sizeClasses[size],
                          sizeClasses[size],
                          "pm-dialog__panel w-full min-w-0 transform rounded-none sm:rounded-[18px] p-3 sm:p-7 text-left align-middle transition-all",
                          "m-0 sm:mb-[5vh] sm:mt-[5vh] h-[var(--vv-height,100dvh)] max-h-[var(--vv-height,100dvh)] sm:h-auto sm:max-h-[85vh] min-h-0 flex flex-col overflow-hidden",
                          panelClassName
                        )
                  }
                >
                  {hasFormHeader && (
                    <div className="pm-dialog__header -mx-3 -mt-3 flex-shrink-0 sm:-mx-7 sm:-mt-7">
                      <div className="min-w-0 flex-1">
                        {eyebrow && <p className="pm-eyebrow mb-2">{t(eyebrow)}</p>}
                        <Dialog.Title as="h3" className="pm-dialog__heading">{t(title)}</Dialog.Title>
                        {subtitle && <p className="pm-dialog__subtitle">{t(subtitle)}</p>}
                      </div>
                      <div className="flex items-center gap-2">{headerControls}</div>
                    </div>
                  )}
                  {hasTitle && !hasFormHeader && (
                    <div
                      className={
                        dense
                          ? "px-2 pt-2 pb-2 sm:px-0 sm:pt-0 sm:pb-2 flex-shrink-0"
                          : "mb-2 sm:mb-4 flex-shrink-0"
                      }
                    >
                      <Dialog.Title
                        as="h3"
                        className="pm-dialog__title flex justify-between items-center gap-3"
                      >
                        <div className="min-w-0">{t(title)}</div>
                        <div className="flex relative right-0 items-center gap-2 text-right">
                          {headerControls}
                        </div>
                      </Dialog.Title>
                    </div>
                  )}
                  <div
                    className={TWclassNames(
                      dense
                        ? "flex-1 min-h-0 overflow-hidden flex flex-col"
                        : hasFormHeader
                          ? "pt-4 sm:pt-5 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
                          : hasTitle
                          ? "mt-1 sm:mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
                          : "flex min-h-0 flex-1 flex-col overflow-hidden",
                      contentClassName
                    )}
                  >
                    {isLoading ? <ResourceLoading /> : children}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
// export const PMDialog = React.memo(
//   PMDialogComponent,
//   (prevProps, nextProps) => {
//     // Only re-render if essential props change
//     return (
//       prevProps.open === nextProps.open &&
//       prevProps.title === nextProps.title &&
//       prevProps.isLoading === nextProps.isLoading &&
//       prevProps.size === nextProps.size &&
//       prevProps.closeOnOutsideClick === nextProps.closeOnOutsideClick
//     );
//   }
// );

export const PMDialog = PMDialogComponent;

PMDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  dense: PropTypes.bool,
  size: PropTypes.oneOf([
    "sm",
    "md",
    "lg",
    "xl",
    "2xl",
    "3xl",
    "4xl",
    "5xl",
    "6xl",
    "7xl",
  ]),
  zIndex: PropTypes.number,
  panelClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.string,
};
